import { useEffect, useState } from 'react';
import { alert, haptic } from '../../lib/telegram';
import { useT } from '../../lib/i18n';
import { api } from './api';

/**
 * This client's prices.
 *
 * A frozen number rather than a percentage, so a long-standing client
 * stays where they are when the standard rate rises. Blank means they pay
 * whatever everyone else pays.
 *
 * Changing a rate never touches an existing booking: amount_due is
 * snapshotted when the booking is made.
 */
export function RatesSection({ clientId, name }: { clientId: string; name: string }) {
  const t = useT();
  const [rates, setRates] = useState<any[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const load = () => api<any[]>(`/api/coach/clients/${clientId}/rates`).then((r) => {
    setRates(r);
    setDraft(Object.fromEntries(r.map((x: any) =>
      [x.session_type_id, x.custom_price == null ? '' : String(x.custom_price)])));
  });

  useEffect(() => { load().catch(() => setRates([])); }, [clientId]);

  if (!rates) return null;

  async function save(r: any) {
    const raw = draft[r.session_type_id];
    const price = raw === '' ? null : Number(raw);
    try {
      const res = await api<{ bookingsUpdated?: number }>(
        `/api/coach/clients/${clientId}/rates/${r.session_type_id}`,
        { method: 'PUT', body: JSON.stringify({ price }) });
      haptic.success();
      const n = res.bookingsUpdated ?? 0;
      // Say what else changed. Silently repricing bookings the coach
      // didn't know about would be worse than not doing it.
      const also = n > 0
        ? t('co.cd.creditsMoved', { n, bookings: t.word('co.cd.bookings', n) })
        : '';
      await alert((price === null
        ? t('co.cd.standardRate')
        : t('co.cd.ownRate', { price, minutes: r.duration_min })) + also);
      load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    }
  }

  return (
    <>
      <div className="section-title">{t('co.cd.ratesTitle')}</div>
      <div className="list">
        {rates.map((r) => {
          const current = r.custom_price == null ? '' : String(r.custom_price);
          const dirty = (draft[r.session_type_id] ?? '') !== current;
          return (
            <div className="card" key={r.session_type_id}>
              <label className="field">
                <span className="field__label">
                  {t('co.cd.minutes', { n: r.duration_min })}
                  <small style={{ display: 'block', color: 'var(--hint)', fontSize: 11 }}>
                    {t('co.cd.standard', { amount: r.standard_price })}
                  </small>
                </span>
                <span className="field__input">
                  <input type="number" inputMode="decimal"
                    placeholder={String(r.standard_price)}
                    value={draft[r.session_type_id] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [r.session_type_id]: e.target.value })} />
                  <em>€</em>
                </span>
              </label>
              {dirty && (
                <button className="savebtn" onClick={() => save(r)}>
                  {(draft[r.session_type_id] ?? '') === '' ? t('co.cd.useStandard') : t('co.cd.savePrice')}
                </button>
              )}
            </div>
          );
        })}
        <div className="card__when" style={{ padding: '4px 2px' }}>
          {t('co.cd.ratesNote')}
        </div>
      </div>
    </>
  );
}
